import { MobileTable, Page, PageTitleWithButton, Pagination, Table } from '../../../components';
import { Theme, Box, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useHistory } from 'react-router-dom';
import { Add, Delete, Edit } from '@mui/icons-material';
import { ManagePartsFilters } from './manage-parts-filters';
import { isMobile } from 'react-device-detect';
import { getPartList } from '../../../fetch';
import { useQuery } from 'react-query';
import { IResponse, IPartListItem } from '../../../models';
import { useState, useMemo } from 'react';
import { CreatePartModal } from './CreatePartModal';

export const ManageParts = () => {
  const classes = useStyles();
  const history = useHistory();
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [sortDirection, setSortDirection] = useState<{
    PartNumber?: 'Asc' | 'Desc';
    Class?: 'Asc' | 'Desc';
    Name?: 'Asc' | 'Desc';
    Active?: 'Asc' | 'Desc';
  }>({
    Name: 'Asc'
  });
  const [selectedSort, setSelectedSort] = useState<string>('name');
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const [selectedPartClassIdFilter, setSelectedPartClassIdFilter] = useState(null);
  const [isModalOpen, showModal] = useState<boolean>(false);

  const [filters, setFilters] = useState<{
    partClassId?: string;
    name?: string;
  }>({});

  const handleEditPart = partId => {
    history.push(`/development/parts/manage-parts/${partId}/general-information`);
  };

  const handleDeletePart = (selectedPart: any) => {
    console.log('delete', selectedPart);
  };

  const {
    isLoading: isLoadingPartsList,
    data: parts,
    isRefetching: refetchingPartsList
  } = useQuery<IResponse<IPartListItem[]>, Error>(
    ['parts-list', page, perPage, filters, selectedSort, sortDirection],
    () =>
      getPartList({
        page: page + 1,
        perPage,
        name: searchValue,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort],
        partClassId: selectedPartClassIdFilter
      }),
    {
      onSuccess: optionSets => {
        setRecordCount(optionSets.totalRecordCount);
      }
    }
  );
  const partsData = useMemo(() => parts?.records ?? [], [parts]);

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Part Number',
        accessor: 'partId',
        isServerSorted: selectedSort === 'PartNumber',
        isServerSortedDesc: sortDirection.PartNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('PartNumber')
      },
      {
        Header: 'Class',
        accessor: 'class',
        isServerSorted: selectedSort === 'Class',
        isServerSortedDesc: sortDirection.Class === 'Desc',
        handleClickColumn: () => handleClickColumn('Class')
      },
      {
        Header: 'Name',
        accessor: 'name',
        isServerSorted: selectedSort === 'Name',
        isServerSortedDesc: sortDirection.Name === 'Desc',
        handleClickColumn: () => handleClickColumn('Name')
      },
      {
        Header: 'Status',
        accessor: 'isActive',
        isServerSorted: selectedSort === 'Active',
        isServerSortedDesc: sortDirection.Active === 'Desc',
        handleClickColumn: () => handleClickColumn('Active'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPartListItem } };
        }) => {
          return <span>{original.isActive ? 'Active' : 'Inactive'}</span>;
        }
      },
      {
        id: 'actions',
        accessor: '',
        Header: ' ',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPartListItem } };
        }) => {
          return (
            <div className={classes.actionButtons}>
              <Button
                id="edit-part"
                type="button"
                color="primary"
                startIcon={<Edit />}
                onClick={() => handleEditPart(original.partId)}
              >
                Edit
              </Button>
              <Button
                id="delete-part"
                type="button"
                className={classes.deleteButton}
                startIcon={<Delete />}
                onClick={() => {
                  handleDeletePart(original);
                }}
              >
                Delete
              </Button>
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partsData]);

  return (
    <>
      <Page title="Manage Parts">
        <PageTitleWithButton
          pageTitleProps={{
            title: 'Manage Parts'
          }}
          button={
            <Box display="flex">
              <Button
                id="add-option"
                startIcon={<Add />}
                color="primary"
                variant="contained"
                className={classes.addPartButton}
                onClick={() => showModal(true)}
              >
                Add Part
              </Button>
            </Box>
          }
        />
        <ManagePartsFilters
          isLoading={isLoadingPartsList || refetchingPartsList}
          setSelectedPartClassIdFilter={setSelectedPartClassIdFilter}
          handleSearch={(val?: string) => {
            setPage(0);
            setFilters({
              ...filters,
              name: val
            });
          }}
          applyFilters={(clearFilters?: boolean) => {
            if (clearFilters) {
              setFilters({
                name: ''
              });
            } else {
              setPage(0);
              setFilters({
                ...filters,
                name: searchValue,
                partClassId: selectedPartClassIdFilter
              });
            }
          }}
          setPage={setPage}
          setHasAppliedFilters={setHasAppliedFilters}
          hasAppliedFilters={hasAppliedFilters}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
        />
        <Table
          columns={columns}
          data={partsData}
          isLoading={isLoadingPartsList}
          hidePagination
          containerClasses="parts-table"
          ResponsiveComponent={isMobile ? MobileTable : undefined}
          mobileProps={{
            fields: columns
              .filter(col => col.Header)
              .map(col => ({
                name: col.Header,
                accessor: col.accessor,
                Cell: col?.Cell
              })),
            handleEdit: (selectedPart: any) => {
              handleEditPart(selectedPart);
            },
            handleDelete: (selectedPart: any) => {
              handleDeletePart(selectedPart.framerId);
            }
          }}
        />
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      </Page>
      <CreatePartModal open={isModalOpen} onClose={() => showModal(false)} />
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    marginTop: '1rem'
  },
  link: {
    textDecoration: 'none'
  },
  addPartButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      width: 'auto'
    }
  },
  actionButtons: {
    textAlign: 'right',
    display: 'flex'
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  }
}));
